import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import classNames from 'classnames';

import { Close } from '@uikit/icons/Close';

import { RewardPoolsReadableSearchParamsOrderBy } from '@shared/api/rewardPools';
import { REWARD_POOL_STATUS, RewardPoolStatus } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';

import * as Dialog from '@radix-ui/react-dialog';

import {
  categoryFilterOptionsInOrder,
  DEFAULT_CATEGORY_FILTER_VALUE,
  RewardPoolsCategoryFilterType,
  useRewardPoolsCategoryFilterQueryState,
} from './RewardPoolsCategoryFilter';
import {
  DEFAULT_SORT_BY_FILTER_VALUE,
  sortByFiltersOptionsInOrder,
  useRewardPoolsOrderByFilterQueryState,
} from './RewardPoolsOrderByFilter';
import {
  DEFAULT_STATUS_FILTER_VALUE,
  statusFiltersInOrder,
  useRewardPoolsStatusFilterQueryState,
} from './RewardPoolsStatusFilter';

const RewardPoolsFiltersDialog = () => {
  const { isAuth } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useRewardPoolsStatusFilterQueryState();
  const [category, setCategory] = useRewardPoolsCategoryFilterQueryState();
  const [sortBy, setSortBy] = useRewardPoolsOrderByFilterQueryState();

  const defaultValues = useMemo(() => {
    return {
      status,
      sortBy,
      category,
    };
  }, [status, sortBy, category]);

  const { register, handleSubmit, reset } = useForm<{
    status: RewardPoolStatus;
    sortBy: RewardPoolsReadableSearchParamsOrderBy;
    category: RewardPoolsCategoryFilterType;
  }>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, isOpen]);

  const handleClearAllButtonClick = () => {
    setStatus(DEFAULT_STATUS_FILTER_VALUE);
    setSortBy(DEFAULT_SORT_BY_FILTER_VALUE);
    setCategory(DEFAULT_CATEGORY_FILTER_VALUE);
  };

  const submit = handleSubmit((data) => {
    setStatus(data.status);
    setSortBy(data.sortBy);
    setCategory(data.category);
    setIsOpen(false);
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <button className="msq-btn msq-btn-lg msq-btn-secondary w-full md:hidden">Filters</button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay">
          <Dialog.Content
            aria-describedby={undefined}
            className="dialog dialog-mobile-menu dialog-sm"
          >
            <div className="dialog-header">
              <Dialog.Title className="dialog-title">Filters</Dialog.Title>

              <Dialog.Close asChild>
                <button className="dialog-close">
                  <Close className="dialog-close-icon" />
                </button>
              </Dialog.Close>
            </div>

            <form onSubmit={submit} id="reward-pools-filters" className="dialog-body">
              <h3 className="sub-menu-title sticky">Sort By</h3>

              {sortByFiltersOptionsInOrder.map(({ value, label }) => {
                return (
                  <label key={value} className="sub-menu-link">
                    <input {...register('sortBy')} className="radio" type="radio" value={value} />
                    {label}
                  </label>
                );
              })}

              <hr className="divider m-1" />

              <h3 className="sub-menu-title">Status</h3>
              {statusFiltersInOrder.map(({ id, label }) => {
                const isDisabled = id === REWARD_POOL_STATUS.CLAIMED && !isAuth;

                return (
                  <label
                    key={id}
                    className={classNames('sub-menu-link', {
                      'sub-menu-link-disabled': isDisabled,
                    })}
                  >
                    <input
                      {...register('status')}
                      disabled={isDisabled}
                      className="radio"
                      type="radio"
                      value={id}
                    />
                    {label}
                  </label>
                );
              })}

              <hr className="divider m-1" />

              <h3 className="sub-menu-title">Pool</h3>
              {categoryFilterOptionsInOrder.map(({ id, label }) => {
                return (
                  <label key={id} className="sub-menu-link">
                    <input {...register('category')} className="radio" type="radio" value={id} />
                    {label}
                  </label>
                );
              })}
            </form>

            <div className="dialog-footer dialog-menu-buttons">
              <button
                onClick={handleClearAllButtonClick}
                className="msq-btn msq-btn-lg msq-btn-secondary"
              >
                Clear All
              </button>
              <button
                type="submit"
                form="reward-pools-filters"
                className="msq-btn msq-btn-lg msq-btn-primary"
              >
                Apply
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RewardPoolsFiltersDialog;
